/**
 * PURPOSE: Filtro de Programación por instituciones participantes (FTR-0040).
 * RESPONSIBILITIES: Identificar la institución de cada lado (idinstitution, nunca idparticipant: la fase
 *   final puede usar otra fila de participante de la misma institución), filtrar los partidos del torneo,
 *   agruparlos por fase + fecha/etapa en orden ascendente con su etiqueta, y construir las opciones del
 *   selector (sin duplicados, sin filas eliminadas y unidas con las instituciones de los partidos).
 * COLLABORATORS: ProgramacionArea.jsx, InstitutionMultiSelect.jsx, programacionTableOrder.js,
 *   participantDisplayName.js.
 * ROUTE: winscore/src/utils/programacionInstitutionFilter.js
 */
import { getParticipantLabel } from "./participantDisplayName.js";
import { isByeMatchRecord } from "./programacionTableOrder.js";

const FINAL_PHASE_ID = 2;
const DELETED_STATE = -2;

const toPositiveId = (value) => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/** Institución de un lado; acepta el shape de getmatches/getparticipants (`Institution`) o el plano. */
export const sideInstitutionId = (participant) =>
  toPositiveId(participant?.idinstitution ?? participant?.Institution?.idinstitution);

export const matchInstitutionIds = (match) => [
  sideInstitutionId(match?.Participant1),
  sideInstitutionId(match?.Participant2),
];

/** Partidos (no BYE) en los que juega alguna institución elegida; cada partido aparece una sola vez. */
export const filterMatchesByInstitutions = (matches = [], selectedIds = []) => {
  const selected = new Set(selectedIds.map(toPositiveId).filter(Boolean));
  if (!selected.size) return [];

  return matches.filter(
    (match) =>
      !isByeMatchRecord(match) && matchInstitutionIds(match).some((id) => id !== null && selected.has(id))
  );
};

const timeSeconds = (value) => {
  const found = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(String(value || "").trim());
  if (!found) return null;
  return Number(found[1]) * 3600 + Number(found[2]) * 60 + Number(found[3] || 0);
};

const compareNullableLast = (a, b) => {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  return a < b ? -1 : 1;
};

const dateKey = (value) => {
  const text = String(value || "").trim();
  return text ? text.slice(0, 10) : null;
};

/** Dentro de una fecha/etapa: fecha → hora (los que no tienen, al final) → nro_match → idmatch. */
export const compareWithinStage = (a, b) =>
  compareNullableLast(dateKey(a?.date_match), dateKey(b?.date_match)) ||
  compareNullableLast(timeSeconds(a?.time_match), timeSeconds(b?.time_match)) ||
  compareNullableLast(
    a?.nro_match === null || a?.nro_match === undefined ? null : Number(a.nro_match),
    b?.nro_match === null || b?.nro_match === undefined ? null : Number(b.nro_match)
  ) ||
  Number(a?.idmatch) - Number(b?.idmatch);

const phaseOrder = (match, includePhase) =>
  includePhase ? (Number(match?.idtypephase) === FINAL_PHASE_ID ? 2 : 1) : 1;

const fechaNumber = (match) => {
  const n = Number(match?.nro_fecha);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/**
 * Etiqueta de un grupo. Fase de grupos: "FECHA N". Fase final: la misma etiqueta de etapa que ya usan
 * los botones de la pantalla (`stageLabels`, índice nro_fecha - 1), "REPECHAJE" para la etapa 1 si el
 * torneo tiene repechaje, y "ETAPA N" si la configuración no la nombra.
 */
const stageLabel = ({ phase, fecha, stageLabels, hasRepechaje }) => {
  if (fecha === null) return phase === 2 ? "ETAPA SIN ASIGNAR" : "SIN FECHA ASIGNADA";
  if (phase !== 2) return `FECHA ${fecha}`;
  if (hasRepechaje && fecha === 1) return "REPECHAJE";
  const label = stageLabels[fecha - 1];
  return (label ? String(label) : `Etapa ${fecha}`).toUpperCase();
};

/**
 * Agrupa los partidos filtrados por fase + fecha/etapa, en orden ascendente: primero todas las fechas
 * de grupos, luego las etapas finales. La clave compuesta es necesaria porque la "Fecha 1" y la etapa 1
 * comparten nro_fecha. Sin filtro de fase (`includePhase = false`) se agrupa solo por nro_fecha.
 */
export const groupMatchesByStage = (
  matches = [],
  { includePhase = true, stageLabels = [], hasRepechaje = false } = {}
) => {
  const groups = new Map();

  for (const match of matches) {
    const phase = phaseOrder(match, includePhase);
    const fecha = fechaNumber(match);
    const key = `${phase}:${fecha ?? "none"}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        phase,
        fecha,
        label: stageLabel({ phase, fecha, stageLabels, hasRepechaje }),
        matches: [],
      });
    }
    groups.get(key).matches.push(match);
  }

  const list = [...groups.values()].sort(
    (a, b) => a.phase - b.phase || compareNullableLast(a.fecha, b.fecha)
  );
  list.forEach((group) => group.matches.sort(compareWithinStage));
  return list;
};

const normalizeSearch = (text) =>
  String(text || "")
    .toLocaleLowerCase("es-PE")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

const institutionOf = (participant) => participant?.Institution ?? participant?.institution ?? null;

/**
 * Opciones del selector: instituciones participantes del evento + deporte (`getparticipants` de fase 1,
 * sin filas eliminadas), sin duplicados por idinstitution, más cualquier institución que aparezca en los
 * partidos del torneo y no esté en esa lista (así ningún partido queda inalcanzable). Etiqueta con la
 * misma regla que la tabla (FTR-0006 + moodName).
 */
export const buildInstitutionOptions = ({ participants = [], matches = [], moodName } = {}) => {
  const byInstitution = new Map();

  const add = (participant) => {
    const id = sideInstitutionId(participant);
    if (!id || byInstitution.has(id)) return;

    const institution = institutionOf(participant) || {};
    const label = String(
      getParticipantLabel(participant, moodName) || institution.name || institution.abrev || `Institución ${id}`
    ).trim();
    const logo = institution.image_path ? `${institution.path_base || ""}${institution.image_path}` : null;

    byInstitution.set(id, {
      value: id,
      label,
      logo,
      search: normalizeSearch(`${label} ${institution.name || ""} ${institution.abrev || ""}`),
    });
  };

  participants.filter((participant) => Number(participant?.state) !== DELETED_STATE).forEach(add);
  matches.forEach((match) => {
    add(match?.Participant1);
    add(match?.Participant2);
  });

  return [...byInstitution.values()].sort(
    (a, b) => a.label.localeCompare(b.label, "es", { sensitivity: "base" }) || a.value - b.value
  );
};

export const matchesInstitutionSearch = (option, inputValue) =>
  String(option?.search || "").includes(normalizeSearch(inputValue));
